import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useApi } from "../../hooks/useApi";
import { FISCAL_IVA_OPTIONS, formatCuitDisplay } from "../../constants/fiscalProfile";

const MY_TENANT_QUERY = `
  query MyTenant {
    myTenant {
      tenant_id
      name
      ingest_email
      storage_prefix
      fiscal_cuit
      fiscal_legal_name
      fiscal_iva_condition
    }
  }
`;

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#fff" }}>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body1" sx={{ wordBreak: "break-all", flex: 1 }}>
          {value || "—"}
        </Typography>
        {value && (
          <Tooltip title={copied ? "Copiado" : "Copiar"}>
            <IconButton size="small" onClick={handleCopy} aria-label={`Copiar ${label}`}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
}

function ivaLabel(value) {
  return FISCAL_IVA_OPTIONS.find((opt) => opt.value === value)?.label || value || "—";
}

export default function WorkspaceProfileSection() {
  const { callApi } = useApi();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadTenant = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await callApi(MY_TENANT_QUERY);
      setTenant(data.myTenant);
    } catch (e) {
      setError(e.message || "No se pudo cargar el workspace");
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  return (
    <div id="workspace" className="settings-workspace-section">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Datos de tu espacio, perfil fiscal e ingesta por email
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={72} />
        </Stack>
      ) : tenant ? (
        <Stack spacing={2}>
          <CopyField label="Nombre del workspace" value={tenant.name} />
          <CopyField label="CUIT" value={formatCuitDisplay(tenant.fiscal_cuit)} />
          <CopyField label="Razón social" value={tenant.fiscal_legal_name} />
          <CopyField label="Condición ante IVA" value={ivaLabel(tenant.fiscal_iva_condition)} />
          <CopyField label="Tenant ID" value={tenant.tenant_id} />
          <CopyField label="Email de ingesta" value={tenant.ingest_email} />
          {tenant.storage_prefix && (
            <Typography variant="caption" color="text.secondary">
              Prefijo S3: {tenant.storage_prefix}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Enviá facturas y documentos como adjuntos a tu email de ingesta; se procesarán
            automáticamente en este workspace.{" "}
            <RouterLink to="/fiscal">Editar datos fiscales</RouterLink>
          </Typography>
        </Stack>
      ) : (
        <Alert severity="info">No hay workspace asignado a tu cuenta.</Alert>
      )}
    </div>
  );
}
